// core module
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

// Application components
import { AuthService } from '@core/services/auth.service';
import { CampaignService } from '@core/services/campaign.service';
import { RequestService } from '@core/services/request.service';
import { CampaignModel } from '@shared/models/campaign.model';

import { CookieService } from 'ngx-cookie-service';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '@app/core/services/language.service';

/**
 * Product component
 */
@Component({
  selector: 'app-campaign-details',
  templateUrl: './campaign-details.component.html',
  styleUrls: ['./campaign-details.component.scss'],
})

export class CampaignDetailsComponent implements OnInit, AfterViewInit {
  campaignId: number;
  @ViewChild('cashbackContainer', { read: ElementRef }) cashbackContainerRef!: ElementRef;
  
  campaign: CampaignModel;
  brandname: string;
  urlPath: string;
  uploadAllowed: boolean;
  notYetStarted: boolean;
  testModus: boolean=false;
  noAuth: boolean=false;

  //baseUrl = 'https://cashbackswebapi.azurewebsites.net';
  private config: any;
  public baseUrl: string = 'https://cashbackswebapicore.whitedune-47840e2f.westeurope.azurecontainerapps.io';  
  siteUrl = 'https://beefund.app';

  username: any;

  /**
   * Life cycle method
   * @param dialog MatDialog
   * @param authService AuthService
   * @param campaignService CampaignService
   * @param requestService RequestService
   * @param route ActivatedRoute
   * @param router Router
   * @param snackBar MatSnackBar
   */
  constructor(
    public dialog: MatDialog,
    private authService: AuthService,
    private campaignService: CampaignService,
    private metaService: Meta,
    private requestService: RequestService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private titleService: Title,
    private cookieService: CookieService,
    private translate: TranslateService,
    private languageService: LanguageService
  ) {}

  ngAfterViewInit(): void {
    const cashbackElement = document.createElement('kbk-upload-receipt') as any;
    cashbackElement.campaignId = this.campaignId;
    cashbackElement.width = this.campaignId;
    this.cashbackContainerRef.nativeElement.appendChild(cashbackElement);
   }

  /**
   * Life cycle init method
   */
  ngOnInit(): void {
    this.campaignId = Number(this.route.snapshot.paramMap.get('id'));
    if (!this.cookieService.check('landing' + this.campaignId)){
      this.cookieService.set('landing' + this.campaignId, window.location.href);
    }
    if (!this.cookieService.check('source' + this.campaignId)){
      this.cookieService.set('source' + this.campaignId, new URLSearchParams(window.location.search).get('utm_source')??'');
    }
    if (!this.cookieService.check('referrer' + this.campaignId)){
      this.cookieService.set('referrer' + this.campaignId, document.referrer);
    }
    //this.noAuth = this.campaignId == 68 && !this.authService.isAuthenticated();
    this.urlPath = this.route.snapshot.paramMap.get('path');
    this.authService.getUsername().subscribe((data) => this.username = data ? data : '');
    if (this.campaignId && this.campaignId !== 0) {
      let lang = this.route.snapshot.queryParams.lang;
      let languageId:string;
      if (lang){
        languageId = this.languageService.getLanguageId(lang.split('-')[0]);
      }
      this.campaignService.getCampaignById(this.campaignId, languageId).subscribe((data: CampaignModel) => {
        this.campaign = data;
        this.brandname = this.campaign.brandName;
        this.testModus = (new URLSearchParams(window.location.search).get('test')??'').toLowerCase()===this.campaign.brandName.toLowerCase();
        if (this.campaign.private && !this.testModus){
          this.router.navigate(['']);
        };
        this.authService.setCampaign(this.campaign);
        this.noAuth = this.campaign.private && !this.authService.isAuthenticated();
        
        this.titleService.setTitle (this.campaign.name);
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() - 14);
        if (new Date(this.campaign.endDate) > currentDate) {
          this.uploadAllowed = true;
        }
        if (new Date(this.campaign.startDate) > new Date()) {
          this.notYetStarted = true;
        }
        this.metaService.addTags([
          {name: 'keywords', content: 'BeeFund ' + this.campaign.brandName},
          {name: 'description', content: 'BeeFund cashbacks ' + this.campaign.description},
          {property: 'og:title', content: this.campaign.description},
          {property: 'og:description', content: this.campaign.description},
          {property: 'og:type', content: 'website'},
          {property: 'og:url', content: this.siteUrl + '/' + this.campaign.urlPath},
          {property: 'og:image', content: this.baseUrl + '/api/images/' + this.campaign.homepageImageId},
        ]);
      });
    } else {
      this.router.navigate(['']);
    }
  }

  /**
   * Function to open dialog
   */
  openUploadDialog(): void {
    if (this.noAuth){
      let params = {auth:1};
      Object.assign(params, this.route.snapshot.queryParams)
      this.router.navigate([`campaigns/${this.urlPath}/${this.campaignId}/upload`], { queryParams: params});
      return;
    }
    this.authService.getUserInfo().subscribe((user) => {
      if (user===null) {
          this.authService.setLoginDialog({ campaignId: this.campaignId, urlPath: this.urlPath });
      }
      else {
        this.requestService.checkDuplicateEmail(this.campaignId, user.email).subscribe((result) => {
          if (!result) {
            this.router.navigate([`campaigns/${this.urlPath}/${this.campaignId}/upload`], { queryParams: this.route.snapshot.queryParams });
          } else {
            this.snackBar.open(this.translate.instant('DUPLICATE_USER_REQUEST'), 'X', { duration: 3000 });
          }
        });
      }
    });
  }

  get productList(): string {
    if (this.campaign.campaignProducts.length>2)
      return this.campaign.campaignProducts.slice(0,this.campaign.campaignProducts.length - 2).map((p) => p.productName).join(', ') + ', ' +
       this.campaign.campaignProducts.slice(-2).map((p) => p.productName).join(' en ');
    else
      return this.campaign.campaignProducts.slice(-2).map((p) => p.productName).join(' en ');
  }
}
